<template>
  <v-app id="app">
    <WorkMaintenanceInformation
      ref="maintenanceInformation"
    />

    <WorkHeaderWrapper
      v-if="isAuthenticated"
      disable-message-add-button
      :show-dashboard="false"
    />
    <div
      id="mainContent"
      :class="getMainClass()"
      :style="{ '--maintenance-height': `${maintenanceInformationHeight}px` }"
      @dragover.prevent
      @drop.prevent
    >
      <WorkErrorNotification />

      <v-container
        :class="{
          'work-main-container pa-0': true,
          'work-main-container--desktop': $vuetify.breakpoint.mdAndUp
        }"
        fluid
      >
        <w-row class="work-main-row pr-0 ma-0">
          <w-column
            v-if="isAuthenticated
              && ($vuetify.breakpoint.mdAndUp || ($vuetify.breakpoint.smAndDown && isSidebarDrawerOpen))"
            :class="{
              'work-main-col pt-0 pl-0': true,
              'work-main-col_sidebar--mobile': $vuetify.breakpoint.smAndDown
            }"
            cols="auto"
          >
            <WorkSidebar 
              shadow 
            />
          </w-column>
          <w-column
            v-if="($vuetify.breakpoint.mdAndUp || ($vuetify.breakpoint.smAndDown && !isSidebarDrawerOpen))"
            :class="{
              'work-main-col': true,
              'work-main-col_content': isAuthenticated,
              'pa-0': !isAuthenticated
            }"
          >
            <div class="work-main-content-wrapper">
              <router-view
                :key="$route.fullPath"
                ref="routerView"
              />
            </div>
          </w-column>
        </w-row>
      </v-container>

      <WorkBeforeLeaveDialog ref="beforeLeaveDialog" />
      <WorkGlobalWarningDialog ref="warningDialog" />
      <WorkGlobalSnackbar />
    </div>
  </v-app>
</template>

<script>
import authMixin from '@/mixins/authentication-mixin.js';
import dynamicData from '@/mixins/api_data/dynamic-data-mixin';
import { mapActions, mapState, mapGetters } from 'vuex';
import WorkBeforeLeaveDialog from '@/components/global_components/dialogs/WorkBeforeLeaveDialog';
import WorkGlobalWarningDialog from '@/components/global_components/dialogs/WorkGlobalWarningDialog.vue';
import WorkHeaderWrapper from '@/components/header_components/WorkHeaderWrapper';
import WorkSidebar from '../replacements/components/sidebar_components/WorkSidebar';
import store from "@/store";

export default {
  name: 'App',
  components: {
    WorkHeaderWrapper,
    WorkErrorNotification: () =>
      import('@/components/alert_components/error/WorkErrorNotification'),
    WorkGlobalSnackbar: () =>
      import('@/components/global_components/snackbar/WorkGlobalSnackbar'),
    WorkMaintenanceInformation: () =>
      import('@/components/alert_components/error/WorkMaintenanceInformation'),
    WorkBeforeLeaveDialog,
    WorkGlobalWarningDialog,
    WorkSidebar
  },
  mixins: [
    authMixin,
    dynamicData('systeminfo', 'system/information'),
  ],
  created() {
    document.addEventListener('keyup', this.keyUpHandler);
    window.addEventListener('popstate', this.handleBrowserBackClick, false);
  },
  mounted() {
    this.$root.$beforeLeaveDialog = this.$refs.beforeLeaveDialog;
    this.$root.$warningDialog = this.$refs.warningDialog;
    this.setTabTitle();
    store.dispatch('sidebarDrawer/setSidebarDrawerIsOpen', false);
    store.dispatch('updateListeners/resetMaintenanceModeUpdateRequired');
  },
  updated() {
    const maintenanceInformation = this.$refs.maintenanceInformation?.$el;
    if (maintenanceInformation && maintenanceInformation instanceof HTMLElement && this.resizeObserver == undefined) {
      const paddingTop = parseInt(window.getComputedStyle(maintenanceInformation).paddingTop, 10);
      const paddingBottom = parseInt(window.getComputedStyle(maintenanceInformation).paddingBottom, 10);
      this.resizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          this.maintenanceInformationHeight = entry.contentRect.height + paddingTop + paddingBottom;
        }
      });
      this.resizeObserver.observe(maintenanceInformation);
    } else if (maintenanceInformation == undefined && this.resizeObserver !== undefined) {
      this.resizeObserver.disconnect();
      this.resizeObserver = undefined;
      this.maintenanceInformationHeight = 0;
    }
  },
  beforeDestroy() {
    this.cleanup();
  },
  deactivated() {
    this.cleanup();
  },
  computed: {
    ...mapState(['activeModule']),
    ...mapGetters('sidebarDrawer', ['isSidebarDrawerOpen']),
    name() {
      return this.data;
    },
    applicationName() {
      return this.systeminfo?.applicationName
    },
  },
  data() {
    return {
      maintenanceInformationHeight: 0,
      resizeObserver: undefined,
    };
  },
  methods: {
    ...mapActions('routingHistory', [
      'redirectToLastHistoryObject',
      'setBrowserBackClicked',
    ]),
    ...mapActions('globalEventListeners', ['keyUpHandler']),
    ...mapActions('updateListeners', ['resetMaintenanceModeUpdateRequired']),
    getMainClass() {
      if (!this.isAuthenticated) {
        return 'public';
      } else {
        return '';
      }
    },
    setTabTitle() {
      document.title = this.applicationName ?? this.$t('LoginView.title.work');
    },
    /*******************************************
     * BROWSER EVENTS
     *******************************************/
    handleBrowserBackClick() {
      // see also routing-history.js and router/index.js
      if (this.activeModule.isWindow) {
        this.setBrowserBackClicked();
      }
    },
    cleanup() {
      document.removeEventListener('keyup', this.keyUpHandler);
      window.removeEventListener('popstate', this.handleBrowserBackClick, false);
      this.resizeObserver?.disconnect();
    },
  },
  watch: {
    applicationName: {
      handler() {
        this.setTabTitle();
      },
      immediate: true
    },
  },
};
</script>

<style lang='scss' src='../../../src/styles/app.scss' />
